import { useState, useEffect } from "react";
import { Table, TableHeader, TableColumn, TableBody, TableRow, TableCell, Button, Chip } from "@heroui/react";
import { Plus, Buildings, User, Trash, Pencil } from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { AddBuildingContactModal } from "./addBuildingContactModal";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";

interface BuildingContactsTableProps {
    buildingId: string;
}

export const BuildingContactsTable = ({ buildingId }: BuildingContactsTableProps) => {
    const [contacts, setContacts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { session } = useSession();

    useEffect(() => {
        console.log("Modal state changed:", isModalOpen);
    }, [isModalOpen]);

    const fetchContacts = async () => {
        try {
            setIsLoading(true);
            const response = await fetchWithAuth(
                `${API_URL}/contact/building/${buildingId}`,
                { method: "GET" },
                session
            );
            const data = await response.json();
            console.log("Fetched contacts:", data);
            setContacts(data);
        } catch (error) {
            console.error("Error fetching contacts:", error);
            toast.error("Er ging iets mis bij het ophalen van de contacten");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchContacts();
    }, [buildingId]);

    const handleEditContact = (contactId: number) => {
        console.log("Editing contact:", contactId);
    };

    const handleAddContact = async (data: any) => {
        try {
            console.log("Adding contact with data:", data);
            const response = await fetchWithAuth(
                `${API_URL}/contact/building/${buildingId}`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(data),
                },
                session
            );
            
            if (response.ok) {
                toast.success("Contact succesvol toegevoegd");
                fetchContacts();
                setIsModalOpen(false);
                console.log("Contact added successfully, modal closed");
            }
        } catch (error) {
            console.error("Error adding contact:", error);
            toast.error("Er ging iets mis bij het toevoegen van het contact");
        }
    };

    const handleDeleteContact = async (contactId: number) => {
        console.log("Attempting to delete contact:", contactId);
        try {
            const response = await fetchWithAuth(
                `${API_URL}/contact/building-contact/${contactId}`,
                {
                    method: "DELETE",
                },
                session
            );

            if (response.ok) {
                toast.success("Contact succesvol verwijderd");
                fetchContacts();
                console.log("Contact deleted successfully");
            }
        } catch (error) {
            console.error("Error deleting contact:", error);
            toast.error("Er ging iets mis bij het verwijderen van het contact");
        }
    };

    if (contacts.length === 0 && !isLoading) {
        return (
            <div className="z-10 flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed border-neutral-200 p-8">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-neutral-100">
                    <User weight="duotone" size={24} className="text-neutral-500" />
                </div>
                <div className="text-center">
                    <h3 className="text-lg font-medium text-neutral-900">Geen contacten gevonden</h3>
                    <p className="mt-1 text-sm text-neutral-500">
                        Begin met het toevoegen van contacten aan dit gebouw
                    </p>
                </div>
                <Button
                    color="primary"
                    startContent={<Plus weight="bold" />}
                    onPress={() => setIsModalOpen(true)}
                >
                    Contact toevoegen
                </Button>

                {isModalOpen && (
                    <AddBuildingContactModal
                        isOpen={isModalOpen}
                        onOpenChange={setIsModalOpen}
                        onSave={handleAddContact}
                    />
                )}
            </div>
        );
    }

    return (
        <div className="z-10 flex flex-col gap-4">
            <div className="flex justify-between">
                <h2 className="text-lg font-medium">Contacten</h2>
                <Button
                    color="primary"
                    startContent={<Plus weight="bold" />}
                    onPress={() => setIsModalOpen(true)}
                >
                    Contact toevoegen
                </Button>
            </div>

            <Table aria-label="Building contacts">
                <TableHeader>
                    <TableColumn>CONTACT</TableColumn>
                    <TableColumn>TYPE</TableColumn>
                    <TableColumn>TAGS</TableColumn>
                    <TableColumn>REDEN</TableColumn>
                    <TableColumn width={50}>ACTIES</TableColumn>
                </TableHeader>
                <TableBody
                    isLoading={isLoading}
                    emptyContent={"Geen contacten gevonden"}
                >
                    {contacts.map((contact: any) => (
                        <TableRow key={contact.id}>
                            <TableCell>
                                <div className="flex items-center gap-2">
                                    {contact.type === 'company' ? (
                                        <Buildings weight="duotone" size={20} />
                                    ) : (
                                        <User weight="duotone" size={20} />
                                    )}
                                    <Link 
                                        to={contact.type === 'company' 
                                            ? `/company/${contact.company.id}`
                                            : `/contact/${contact.contact.id}`
                                        }
                                        className="text-moss-primary hover:underline"
                                    >
                                        {contact.type === 'company'
                                            ? contact.company.name
                                            : `${contact.contact.firstName} ${contact.contact.lastName}`}
                                    </Link>
                                </div>
                            </TableCell>
                            <TableCell>
                                {contact.type === 'company' ? 'Bedrijf' : 'Contact'}
                            </TableCell>
                            <TableCell>
                                <div className="flex flex-wrap gap-1">
                                    {contact.type === 'company' 
                                        ? contact.company.tags?.map((tag: any) => (
                                            <Chip 
                                                key={tag} 
                                                size="sm"
                                                variant="flat"
                                                classNames={{
                                                    base: "bg-moss-primary/10 text-moss-primary text-xs",
                                                }}
                                            >
                                                {tag}
                                            </Chip>
                                        ))
                                        : contact.contact.tags?.map((tag: any) => (
                                            <Chip 
                                                key={tag} 
                                                size="sm"
                                                variant="flat"
                                                classNames={{
                                                    base: "bg-moss-primary/10 text-moss-primary text-xs",
                                                }}
                                            >
                                                {tag}
                                            </Chip>
                                        ))
                                    }
                                </div>
                            </TableCell>
                            <TableCell>{contact.reason}</TableCell>
                            <TableCell>
                                <div className="flex gap-2">
                                    <Button
                                        isIconOnly
                                    size="sm"
                                    variant="light"
                                    onPress={() => handleDeleteContact(contact.id)}
                                >
                                    <Trash size={20} className="text-danger" />
                                </Button>
                                <Button
                                    isIconOnly
                                    size="sm"
                                    variant="light"
                                    onPress={() => handleEditContact(contact.id)}
                                >
                                        <Pencil size={20} className="text-neutral" />
                                    </Button>
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {isModalOpen && (
                <AddBuildingContactModal
                    isOpen={isModalOpen}
                    onOpenChange={setIsModalOpen}
                    onSave={handleAddContact}
                />
            )}
        </div>
    );
};

